import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, Box, Typography, TextField, MenuItem,
  IconButton, Tooltip, Button, Chip,
} from '@mui/material';
import { Close, Edit, Delete, Add, InfoOutlined } from '@mui/icons-material';
import ConfirmModal from './ConfirmModal';
import SavingOverlay from '../SavingOverlay';
import {
  patientTherapyPlanService, therapyPlanErrorMessage,
  TherapyPeriodRow, TherapyFilterOption,
} from '../../services/patientTherapyPlanService';
import { colors, typography } from '../../theme/designSystem';

/**
 * Manutenção dos períodos do plano terapêutico (RN-541c). Substitui a modal "Períodos de
 * execução" do Bubble: o formulário à esquerda, a lista do que já existe à direita.
 *
 * Gravar aqui grava no Bubble primeiro (RN-302) — ele ainda é o master do módulo.
 */
interface Props {
  isOpen: boolean;
  patientId: string;
  periods: TherapyPeriodRow[];
  professionals: TherapyFilterOption[];
  onClose: () => void;
  onSaved: () => void;
  onError: (mensagem: string) => void;
}

const SITUACAO: Record<string, { label: string; bg: string; color: string }> = {
  vigente: { label: 'Vigente', bg: '#d4edda', color: '#155724' },
  futuro: { label: 'Futuro', bg: '#e3f2fd', color: '#0d47a1' },
  encerrado: { label: 'Encerrado', bg: '#e9ecef', color: '#495057' },
};

const MAX_TITULO = 255;
const MAX_DESCRICAO = 2000;

const formatarData = (iso: string | null): string => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

const tituloPadrao = (inicio: string, fim: string): string =>
  `Personalizado de ${formatarData(inicio || null)} até ${formatarData(fim || null)}`;

const hojeIso = (): string => new Date().toISOString().slice(0, 10);
const emSeisMesesIso = (): string => {
  const d = new Date();
  d.setMonth(d.getMonth() + 6);
  return d.toISOString().slice(0, 10);
};

const TherapyPeriodsModal: React.FC<Props> = ({
  isOpen, patientId, periods, professionals, onClose, onSaved, onError,
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [initialDate, setInitialDate] = useState(hojeIso());
  const [finalDate, setFinalDate] = useState(emSeisMesesIso());
  const [responsibleIds, setResponsibleIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<TherapyPeriodRow | null>(null);
  const [deleting, setDeleting] = useState(false);

  const limpar = () => {
    setEditingId(null);
    setTitle(tituloPadrao(hojeIso(), emSeisMesesIso()));
    setDescription('');
    setInitialDate(hojeIso());
    setFinalDate(emSeisMesesIso());
    setResponsibleIds([]);
    setFormError(null);
  };

  useEffect(() => {
    if (isOpen) limpar();
  }, [isOpen]);

  const editar = (row: TherapyPeriodRow) => {
    setEditingId(row.id);
    setTitle(row.title);
    setDescription(row.description ?? '');
    setInitialDate(row.initialDate ?? hojeIso());
    setFinalDate(row.finalDate ?? emSeisMesesIso());
    setResponsibleIds(row.responsibleIds);
    setFormError(null);
  };

  const salvar = async () => {
    setFormError(null);
    if (!title.trim()) {
      setFormError('Informe o título do período.');
      return;
    }
    if (!initialDate || !finalDate) {
      setFormError('Informe a data inicial e a final.');
      return;
    }
    if (finalDate < initialDate) {
      setFormError('A data final não pode ser anterior à inicial.');
      return;
    }

    setSaving(true);
    try {
      await patientTherapyPlanService.savePeriod({
        id: editingId ?? undefined,
        patientId,
        title: title.trim(),
        description: description.trim() || null,
        initialDate: `${initialDate}T00:00:00Z`,
        finalDate: `${finalDate}T00:00:00Z`,
        responsibleIds,
      });
      limpar();
      onSaved();
    } catch (e) {
      setFormError(therapyPlanErrorMessage(e, 'Não foi possível salvar o período.'));
    } finally {
      setSaving(false);
    }
  };

  const excluir = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await patientTherapyPlanService.deletePeriod(deleteTarget.id);
      setDeleteTarget(null);
      onSaved();
    } catch (e) {
      setDeleteTarget(null);
      onError(therapyPlanErrorMessage(e, 'Não foi possível excluir o período.'));
    } finally {
      setDeleting(false);
    }
  };

  const nomePorId = useMemo(() => {
    const mapa = new Map<string, string>();
    professionals.forEach(p => mapa.set(p.id, p.name));
    return mapa;
  }, [professionals]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.3)', maxHeight: '90vh' } }}
    >
      <DialogTitle
        sx={{
          backgroundColor: colors.primary, color: colors.white, padding: '1.5rem',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}
      >
        <Typography variant="h6" component="h3" sx={{ fontSize: '1.4rem', fontWeight: typography.fontWeight.semibold }}>
          Períodos personalizados
        </Typography>
        <IconButton onClick={onClose} sx={{ color: colors.white, padding: '0.25rem' }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: '1.5rem !important', paddingTop: '1.5rem !important' }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {/* Formulário: no celular ocupa a linha inteira e a lista vem abaixo */}
          <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 320px' }, minWidth: 0 }}>
            <TextField
              fullWidth
              size="small"
              label="Título"
              value={title}
              onChange={e => setTitle(e.target.value)}
              inputProps={{ maxLength: MAX_TITULO }}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 1.5 }}
            />
            <TextField
              fullWidth
              size="small"
              label="Descrição"
              value={description}
              onChange={e => setDescription(e.target.value)}
              inputProps={{ maxLength: MAX_DESCRICAO }}
              InputLabelProps={{ shrink: true }}
              multiline
              minRows={2}
              sx={{ mb: 1.5 }}
            />
            <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                type="date"
                label="Data inicial"
                value={initialDate}
                onChange={e => {
                  setInitialDate(e.target.value);
                  if (title.startsWith('Personalizado de ')) {
                    setTitle(tituloPadrao(e.target.value, finalDate));
                  }
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: '1 1 140px' }}
              />
              <TextField
                size="small"
                type="date"
                label="Data final"
                value={finalDate}
                onChange={e => {
                  setFinalDate(e.target.value);
                  if (title.startsWith('Personalizado de ')) {
                    setTitle(tituloPadrao(initialDate, e.target.value));
                  }
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: '1 1 140px' }}
              />
            </Box>
            <TextField
              select
              fullWidth
              size="small"
              label="Administradores"
              value={responsibleIds}
              onChange={e => {
                const v = e.target.value as unknown as string[];
                setResponsibleIds(typeof v === 'string' ? [v] : v);
              }}
              SelectProps={{
                multiple: true,
                renderValue: (selected) => (
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {(selected as string[]).map(id => (
                      <Chip key={id} label={nomePorId.get(id) ?? id} size="small" sx={{ height: 22 }} />
                    ))}
                  </Box>
                ),
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 1.5 }}
            >
              {professionals.map(p => (
                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
              ))}
            </TextField>

            {formError && (
              <Typography sx={{ color: colors.error, fontSize: typography.fontSize.sm, mb: 1.5 }}>
                {formError}
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                fullWidth
                onClick={salvar}
                disabled={saving}
                startIcon={editingId ? <Edit /> : <Add />}
              >
                {editingId ? 'Salvar alteração' : 'Cadastrar'}
              </Button>
              {editingId && (
                <Button variant="outlined" onClick={limpar} disabled={saving}>
                  Cancelar
                </Button>
              )}
            </Box>
          </Box>

          {/* Lista dos períodos existentes */}
          <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 420px' }, minWidth: 0 }}>
            <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.textSecondary, mb: 1 }}>
              {periods.length} período(s) deste paciente
            </Typography>

            {periods.length === 0 ? (
              <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.textSecondary, py: 3, textAlign: 'center' }}>
                Nenhum período próprio. Os períodos da clínica já ficam disponíveis sem cadastro.
              </Typography>
            ) : (
              <Box sx={{ maxHeight: '420px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
                {periods.map(row => {
                  const s = SITUACAO[row.situation] ?? SITUACAO.vigente;
                  return (
                    <Box
                      key={row.id}
                      sx={{
                        border: '1px solid #e0e0e0', borderRadius: '8px', p: 1.25,
                        display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap',
                        backgroundColor: colors.white,
                      }}
                    >
                      <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 0.25 }}>
                          <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: colors.text }}>
                            {row.title}
                          </Typography>
                          <Chip
                            label={s.label}
                            size="small"
                            sx={{ height: 20, fontSize: '0.7rem', backgroundColor: s.bg, color: s.color }}
                          />
                        </Box>
                        <Typography sx={{ fontSize: '0.78rem', color: colors.textSecondary }}>
                          {formatarData(row.initialDate)} a {formatarData(row.finalDate)}
                          {row.goals.length > 0 && ` · ${row.goals.length} objetivo(s)`}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Editar período">
                          <IconButton size="small" onClick={() => editar(row)} sx={{ color: colors.primary }}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir período">
                          <IconButton size="small" onClick={() => setDeleteTarget(row)} sx={{ color: colors.error }}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start', backgroundColor: '#e6f7f9', border: '1px solid #b8e6ec', borderRadius: '8px', p: 1, mt: 1.5 }}>
              <InfoOutlined sx={{ fontSize: 18, color: colors.primary, mt: '1px' }} />
              <Typography sx={{ fontSize: typography.fontSize.xs, color: colors.textPrimary }}>
                Aqui ficam apenas os ciclos <strong>personalizados</strong> deste paciente, para
                quando ele não couber na régua da clínica. O título nasce das datas e pode ser
                complementado. Os períodos da clínica (como "2026 - 1º Semestre") já aparecem na
                escolha do objetivo e são mantidos pelo administrador. Período com objetivos
                dentro não pode ser excluído.
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <ConfirmModal
        isOpen={deleteTarget !== null}
        title="Excluir período"
        message={`O período "${deleteTarget?.title}" sai da lista. Se ainda houver objetivos nele, a exclusão será recusada.`}
        confirmButtonText="Excluir"
        cancelButtonText="Cancelar"
        type="danger"
        onConfirm={excluir}
        onCancel={() => setDeleteTarget(null)}
        isProcessing={deleting}
      />

      <SavingOverlay open={saving} message="Salvando período..." />
    </Dialog>
  );
};

export default TherapyPeriodsModal;
